/* eslint-disable react/jsx-no-bind */
import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Tooltip,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useBrowserCallAgent } from "src/context/browserCall/BrowserCallAgentContext";
import useCallTone from "src/hooks/useCallTone";

const channelLabel = (c: string) => {
  switch (c) {
    case "whatsapp":
      return "WhatsApp";
    case "facebook":
      return "Facebook";
    case "instagram":
      return "Instagram";
    default:
      return "Web";
  }
};

const IncomingBrowserCallToast: React.FC = () => {
  const { incomingQueue, activeRoomId, acceptCall, rejectCall, focusPopout } =
    useBrowserCallAgent();

  // Ring while there's at least one unanswered incoming call. Same ringtone
  // the WhatsApp call feature uses for consistency.
  useCallTone(incomingQueue.length > 0 ? "incoming" : "idle");

  if (!incomingQueue.length && !activeRoomId) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: 320,
      }}
    >
      {activeRoomId && (
        <Paper
          elevation={6}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #16a34a40",
            background: "linear-gradient(90deg,#dcfce7,#f0fdf4)",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CallIcon sx={{ color: "#16a34a" }} fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              Browser call in progress
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#475569" }}>
              Active in pop-out window
            </Typography>
          </Box>
          <Tooltip title="Focus call window">
            <IconButton size="small" onClick={focusPopout}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      {incomingQueue.map((call) => (
        <Paper
          key={call.roomId}
          elevation={8}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #2563eb40",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1,
            animation: "browserCallPulse 1.4s ease-in-out infinite",
            "@keyframes browserCallPulse": {
              "0%,100%": { boxShadow: "0 0 0 0 rgba(37,99,235,0.45)" },
              "50%": { boxShadow: "0 0 0 8px rgba(37,99,235,0)" },
            },
          }}
        >
          <Avatar sx={{ bgcolor: "#2563eb", width: 36, height: 36 }}>
            {(call.customerName || "?").slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {call.customerName || "Guest caller"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#475569" }}>
              Incoming browser {call.mediaMode} call ·{" "}
              {channelLabel(call.channel)}
            </Typography>
          </Box>
          <Tooltip title="Accept">
            <IconButton
              size="small"
              sx={{
                bgcolor: "#16a34a",
                color: "#fff",
                "&:hover": { bgcolor: "#15803d" },
              }}
              onClick={() => acceptCall(call.roomId)}
            >
              <CallIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton
              size="small"
              sx={{
                bgcolor: "#dc2626",
                color: "#fff",
                "&:hover": { bgcolor: "#b91c1c" },
              }}
              onClick={() => rejectCall(call.roomId)}
            >
              <CallEndIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      ))}
    </Box>
  );
};

export default IncomingBrowserCallToast;
